
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearchState } from '@/hooks/useSearchState';
import { formatDateForUrl } from '@/utils/dateParser';
import { useAmplitudeEvents } from '@/utils/amplitudeEvents';
import { useDeviceDetection } from '@/hooks/useDeviceDetection';

interface UseSearchFormLogicProps {
  variant?: 'default' | 'booking';
  onDateChange?: (checkIn: Date | undefined, checkOut: Date | undefined) => void;
  selectedCheckIn?: Date | undefined;
  selectedCheckOut?: Date | undefined;
  maxGuests?: number;
}

export const useSearchFormLogic = ({
  variant = 'default',
  onDateChange,
  selectedCheckIn,
  selectedCheckOut,
  maxGuests = 6
}: UseSearchFormLogicProps) => {
  const navigate = useNavigate();
  const deviceInfo = useDeviceDetection();
  // Default guests to "1" for new searches
  const searchState = useSearchState(selectedCheckIn, selectedCheckOut, '1');
  const hasInitialized = useRef(false);
  const { trackSearchCompleted } = useAmplitudeEvents();

  // Initialize from URL parameters ONLY ONCE on mount
  useEffect(() => {
    if (!hasInitialized.current) {
      searchState.updateFromUrlParams();
      hasInitialized.current = true;
    }
  }, []); // Empty dependency array - only run once

  // Sync external dates with internal state (for booking variant)
  useEffect(() => {
    if (variant === 'booking') {
      if (selectedCheckIn !== undefined && selectedCheckIn !== searchState.checkIn) {
        searchState.setCheckIn(selectedCheckIn);
      }
      if (selectedCheckOut !== undefined && selectedCheckOut !== searchState.checkOut) {
        searchState.setCheckOut(selectedCheckOut);
      }
    }
  }, [selectedCheckIn, selectedCheckOut, variant, searchState]);

  const handleCheckInChange = useCallback((date: Date | undefined) => {
    searchState.setCheckIn(date);
    onDateChange?.(date, searchState.checkOut);
  }, [searchState, onDateChange]);

  const handleCheckOutChange = useCallback((date: Date | undefined) => {
    searchState.setCheckOut(date);
    onDateChange?.(searchState.checkIn, date);
  }, [searchState, onDateChange]);

  // Enhanced guest change handler with mobile-specific error handling
  const handleGuestsChange = useCallback((newGuests: string) => {
    console.log('🔄 useSearchFormLogic guests changing:', { from: searchState.guests, to: newGuests, isMobile: deviceInfo.isMobile });
    
    try {
      // For mobile Chrome, add a small delay to prevent DOM conflicts
      if (deviceInfo.isMobile && deviceInfo.isAndroid) {
        setTimeout(() => {
          searchState.setGuests(newGuests);
        }, 10);
      } else {
        searchState.setGuests(newGuests);
      }
    } catch (error) {
      console.error('❌ Error setting guests in useSearchFormLogic:', error);
      // Recovery: set to safe default
      searchState.setGuests('1');
    }
  }, [searchState, deviceInfo.isMobile, deviceInfo.isAndroid]);

  const handleSearch = useCallback(() => {
    // Track Search Completed event
    trackSearchCompleted(searchState.checkIn, searchState.checkOut, searchState.guests);
    
    const searchParams = new URLSearchParams();
    if (searchState.checkIn) searchParams.set('checkin', formatDateForUrl(searchState.checkIn));
    if (searchState.checkOut) searchParams.set('checkout', formatDateForUrl(searchState.checkOut));
    if (searchState.guests) searchParams.set('guests', searchState.guests);
    navigate(`/search?${searchParams.toString()}`);
  }, [navigate, trackSearchCompleted, searchState]);

  return {
    checkIn: searchState.checkIn,
    checkOut: searchState.checkOut,
    guests: searchState.guests,
    setGuests: handleGuestsChange,
    handleCheckInChange,
    handleCheckOutChange,
    handleSearch
  };
};
